import { Link } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, MapPin, Phone, MessageCircle, Pizza, SlidersHorizontal, Bike, Star, Check } from 'lucide-react';
import * as Icons from 'lucide-react';
import { getReviews, getPublicMenu, resolveMediaUrl } from '../../lib/api';
import { useClinicBranding } from '../../context/ClinicBrandingContext';
import { buildFooterScheduleLines } from '../../lib/scheduleDisplay';
import { displayBrandName } from '../../lib/brand';
import { useScrollProgress, useReducedMotion, useInView } from '../../lib/motion';
import { isPizzaItem } from '../../lib/cartUtils';
import PizzaArt, { Steam } from '../../components/pizzeria/PizzaArt';
import { IngredientSprite } from '../../components/pizzeria/Ingredients';
import Marquee from '../../components/pizzeria/Marquee';
import Reveal from '../../components/pizzeria/Reveal';
import { telHref, waHref } from '../../components/pizzeria/PizzeriaChrome';
import { useLegalTerms } from '../../lib/legalIdentity';

const HERO_FLOATERS = [
  { type: 'basil', size: 54, top: '4%', left: '6%', depth: 18, dur: 7, rot: -20 },
  { type: 'tomato', size: 46, top: '10%', left: '82%', depth: -24, dur: 6, rot: 10 },
  { type: 'pepperoni', size: 42, top: '78%', left: '4%', depth: 26, dur: 8, rot: 0 },
  { type: 'mushroom', size: 48, top: '84%', left: '72%', depth: -16, dur: 6.5, rot: 15 },
  { type: 'jalapeno', size: 36, top: '44%', left: '-2%', depth: 30, dur: 5.5, rot: 0 },
  { type: 'basil', size: 40, top: '48%', left: '94%', depth: -30, dur: 7.5, rot: 40 },
];

function priceFrom(item) {
  if (isPizzaItem(item) && item.sizes?.length) {
    return Math.min(...item.sizes.map((s) => Number(s.price) || 0));
  }
  return Number(item.price) || 0;
}

/** #3 Título palabra por palabra + subrayado dibujado en la palabra destacada */
function HeroTitle({ prefix, highlight }) {
  const words = String(prefix || '').split(/\s+/).filter(Boolean);
  return (
    <h1 className="t-words font-heading text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-7xl font-extrabold text-[#2B1A12]">
      <span className="sr-only">
        {prefix} {highlight}
      </span>
      <span aria-hidden="true">
        {words.map((w, i) => (
          <span key={`${w}-${i}`} className="t-word" style={{ '--i': i }}>
            <span>{w}&nbsp;</span>
          </span>
        ))}
        <span className="t-word relative" style={{ '--i': words.length }}>
          <span className="italic text-[#C2410C]">{highlight}</span>
        </span>
        <svg
          className="t-underline block w-[min(15rem,60%)] h-4 -mt-1 ml-auto mr-[10%] sm:ml-[42%] sm:mr-0"
          viewBox="0 0 300 20"
          fill="none"
          aria-hidden="true"
          style={{ '--d': `${0.25 + words.length * 0.08}s` }}
        >
          <path d="M4 14 C 60 4, 120 4, 170 10 S 260 16, 296 6" stroke="#F2B33D" strokeWidth="7" strokeLinecap="round" />
        </svg>
      </span>
    </h1>
  );
}

/** #1 pizza que gira (idle + scroll) · #2 ingredientes flotantes con parallax · #13 vapor */
function HeroPizza({ photo, brand }) {
  const wrapRef = useRef(null);
  const p = useScrollProgress(wrapRef, { start: 0.35, end: 0 });
  const reduced = useReducedMotion();

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || reduced || !window.matchMedia?.('(pointer: fine)').matches) return undefined;
    let raf = 0;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const mx = e.clientX / window.innerWidth - 0.5;
        const my = e.clientY / window.innerHeight - 0.5;
        el.style.setProperty('--mx', mx.toFixed(3));
        el.style.setProperty('--my', my.toFixed(3));
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reduced]);

  const rot = reduced ? 0 : p * 240;
  const ty = reduced ? 0 : p * 90;
  const sc = reduced ? 1 : 1 - p * 0.12;

  return (
    <div ref={wrapRef} className="relative mx-auto w-[min(88vw,520px)] aspect-square" style={{ '--mx': 0, '--my': 0 }}>
      <div className="absolute inset-[6%] rounded-full bg-[#F4511E]" aria-hidden="true" />
      <div className="absolute inset-[3%] rounded-full border-[3px] border-dashed border-[#F4511E]/40" aria-hidden="true" />
      <div
        className="absolute inset-[10%] will-change-transform"
        style={{ transform: `translateY(${ty}px) rotate(${rot}deg) scale(${sc})` }}
      >
        <div className="w-full h-full t-spin-idle">
          <PizzaArt className="w-full h-full drop-shadow-[0_24px_30px_rgba(43,26,18,.35)]" />
        </div>
      </div>
      <Steam className="absolute left-1/2 -translate-x-1/2 -top-[4%] w-[34%] opacity-80" />
      {HERO_FLOATERS.map((f, i) => (
        <span
          key={i}
          className="absolute pointer-events-none"
          style={{
            top: f.top,
            left: f.left,
            transform: `translate(calc(var(--mx) * ${f.depth}px), calc(var(--my) * ${f.depth}px + ${-p * f.depth * 3}px))`,
          }}
          aria-hidden="true"
        >
          <IngredientSprite
            type={f.type}
            size={f.size}
            className="t-float drop-shadow-md"
            style={{ '--dur': `${f.dur}s`, '--delay': `${i * -0.8}s`, '--rot': `${f.rot}deg` }}
          />
        </span>
      ))}
      <span className="t-sticker t-sticker-pop t-wiggle absolute right-[2%] top-[14%] text-2xl w-24 h-24 !rounded-full flex-col" style={{ '--d': '.9s' }}>
        2x1
        <span className="text-xs font-bold font-body mt-1">en pizzas</span>
      </span>
      {photo ? (
        <figure className="absolute -bottom-[2%] left-[2%] w-[34%] rotate-[-7deg] bg-white p-1.5 pb-6 rounded-md shadow-xl t-sticker-pop" style={{ '--d': '1.1s' }}>
          <img src={photo} alt={`Pizza de ${brand}`} className="w-full aspect-square object-cover rounded-sm" fetchPriority="high" />
          <figcaption className="font-hand text-[#2B1A12] text-lg text-center leading-none mt-1">¡recién salida!</figcaption>
        </figure>
      ) : null}
    </div>
  );
}

/** #12 «Cómo funciona»: línea punteada que se dibuja con el scroll y pasos que aparecen */
function HowItWorks() {
  const ref = useRef(null);
  const p = useScrollProgress(ref, { start: 0.85, end: 0.45 });
  const [inRef, inView] = useInView();
  const steps = [
    { icon: Pizza, title: 'Elige tu pizza', text: 'Especialidades, bebidas y extras en un solo menú.' },
    { icon: SlidersHorizontal, title: 'Personalízala', text: 'Tamaño, mitad y mitad e ingredientes extra.' },
    { icon: Bike, title: 'Recíbela caliente', text: 'A domicilio o recógela en sucursal.' },
  ];
  return (
    <section ref={ref} className="py-16 md:py-24 t-paper" aria-labelledby="como-funciona">
      <div className="max-w-6xl mx-auto px-4">
        <p className="font-hand text-2xl text-[#C2410C] text-center">así de fácil</p>
        <h2 id="como-funciona" className="font-heading text-4xl md:text-5xl font-extrabold text-center mb-12">
          ¿Cómo funciona?
        </h2>
        <div ref={inRef} className={`relative grid gap-10 md:grid-cols-3 ${inView ? 'is-inview' : ''}`}>
          <svg
            className="hidden md:block absolute left-[16%] right-[16%] top-10 w-[68%] h-6"
            viewBox="0 0 600 24"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0 12 C 100 -4, 200 28, 300 12 S 500 -4, 600 12"
              fill="none"
              stroke="#F4511E"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="640"
              strokeDashoffset={640 * (1 - p)}
            />
          </svg>
          {steps.map((s, i) => {
            const Ico = s.icon;
            return (
              <div key={s.title} className="relative text-center">
                <div
                  className="t-step-icon mx-auto w-20 h-20 rounded-full bg-[#FFFDF8] border-[3px] border-[#2B1A12] shadow-[4px_4px_0_#2B1A12] flex items-center justify-center relative z-[1]"
                  style={{ '--i': i }}
                >
                  <Ico className="w-9 h-9 text-[#C2410C]" aria-hidden="true" />
                  <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#F2B33D] border-2 border-[#2B1A12] font-heading font-extrabold flex items-center justify-center">
                    {i + 1}
                  </span>
                </div>
                <h3 className="font-heading text-2xl font-bold mt-5">{s.title}</h3>
                <p className="text-[#6B5444] mt-2 max-w-xs mx-auto">{s.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ParallaxImage({ src, alt }) {
  const ref = useRef(null);
  const p = useScrollProgress(ref, { start: 1, end: 0 });
  const reduced = useReducedMotion();
  return (
    <div ref={ref} className="relative overflow-hidden rounded-[28px] h-[320px] md:h-[440px] shadow-xl">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 w-full h-[120%] object-cover will-change-transform"
        style={{ transform: reduced ? 'none' : `translateY(${-p * 16}%)` }}
      />
    </div>
  );
}

function iconFromApiName(name) {
  const pascal = String(name || 'star')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('');
  return Icons[pascal] || Icons.Star;
}

function FinalCta({ title, sub, label, path }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="relative overflow-hidden bg-[#F4511E] text-white" aria-labelledby="cta-final">
      <div className="max-w-4xl mx-auto px-4 pt-16 pb-48 md:pb-56 text-center relative z-[1]">
        <h2 id="cta-final" className="font-heading text-4xl md:text-6xl font-extrabold">
          {title}
        </h2>
        <p className="text-lg md:text-xl mt-3 font-semibold">{sub}</p>
        <Link to={path} className="t-btn t-btn-offset mt-8 text-lg" data-testid="cta-book-btn">
          {label} <ArrowRight className="w-5 h-5" aria-hidden="true" />
        </Link>
      </div>
      <div
        className="absolute left-1/2 bottom-0 w-[340px] md:w-[440px] aspect-square transition-transform duration-700"
        style={{
          transform: `translate(-50%, ${inView ? '52%' : '100%'})`,
          transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
        aria-hidden="true"
      >
        <div className="w-full h-full t-spin-idle">
          <PizzaArt className="w-full h-full drop-shadow-[0_-10px_30px_rgba(43,26,18,.35)]" />
        </div>
      </div>
    </section>
  );
}

export default function PizzeriaHome() {
  const { info, schedule } = useClinicBranding();
  const [featured, setFeatured] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    getReviews()
      .then((r) => setReviews(Array.isArray(r) ? r : []))
      .catch(() => {});
    getPublicMenu()
      .then((menu) => {
        const flat = (menu.categories || []).flatMap((c) => c.items || []);
        const pizzas = flat.filter(isPizzaItem);
        const pool = pizzas.length >= 3 ? pizzas : flat;
        const ranked = [...pool].sort((a, b) => Number(Boolean(b.image_url)) - Number(Boolean(a.image_url)));
        setFeatured(ranked.slice(0, 6));
      })
      .catch(() => setFeatured([]));
  }, []);

  const brand = displayBrandName(info?.name);
  const heroBadge = info?.hero_badge_text || 'Pizza artesanal';
  const heroPrefix = info?.hero_title_prefix ?? 'Sabor que';
  const heroHighlight = info?.hero_title_highlight ?? 'conquista';
  const heroSubtitle =
    info?.hero_subtitle ?? 'Pizza recién hecha, ingredientes frescos y entrega a domicilio. Pide por web o WhatsApp.';
  const heroPhoto = info?.hero_image_url?.trim() ? resolveMediaUrl(info.hero_image_url.trim()) : '';
  const heroCtaPrimary = info?.hero_cta_primary_label ?? 'Ordenar ahora';
  const heroCtaPrimaryPath = info?.hero_cta_primary_path ?? '/pedir';
  const heroCtaSecondary = info?.hero_cta_secondary_label ?? 'Ver menú';
  const heroCtaSecondaryPath = info?.hero_cta_secondary_path ?? '/pedir';
  const prodTitle = info?.featured_products_title || 'Del menú';
  const prodIntro = info?.featured_products_intro || 'Algunas de nuestras pizzas favoritas';
  const servicesTitle = info?.services_section_title ?? '¿Por qué elegirnos?';
  const servicesIntro = info?.services_section_intro ?? 'Calidad, rapidez y el sabor que buscas en cada rebanada.';
  const services = Array.isArray(info?.services_bullets) ? info.services_bullets.filter((s) => String(s).trim()) : [];
  const features = Array.isArray(info?.home_features) ? info.home_features : [];
  const clinicImg = info?.clinic_section_image_url?.trim() ? resolveMediaUrl(info.clinic_section_image_url.trim()) : '';
  const reviewsTitle = info?.reviews_section_title ?? 'Lo que dicen nuestros clientes';
  const ctaTitle = info?.cta_title ?? '¿Antojo de pizza?';
  const ctaSub = info?.cta_subtitle ?? 'Ordena en minutos. Te avisamos cuando va en camino.';
  const ctaLabel = info?.cta_button_label ?? 'Pide tu pizza';
  const ctaPath = info?.cta_button_path ?? '/pedir';
  const phone = info?.phone || '';
  const address = info?.address || '';
  const wa = waHref(info?.whatsapp || phone);
  const legalTerms = useLegalTerms();
  const scheduleLines = useMemo(() => buildFooterScheduleLines(schedule), [schedule]);
  const avg = reviews.length ? reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length : 0;

  const marqueeItems = ['Masa fresca diaria', 'Pide por web o WhatsApp', '2x1 en pizzas', 'Una sola pizza −30%', 'Especialidades de la casa'];

  const promos = [
    { sticker: '2x1', tone: '', title: 'En pizzas', text: 'Por cada dos pizzas, la de menor precio es gratis. Se aplica solo en el carrito.', bg: 'bg-[#F2B33D]' },
    { sticker: '−30%', tone: 't-sticker--tomato', title: 'Una sola pizza', text: '¿Solo se te antoja una? Te descontamos el 30%.', bg: 'bg-[#FFFDF8]' },
    { sticker: '½ + ½', tone: 't-sticker--basil', title: 'Mitad y mitad', text: 'Combina dos especialidades en una pizza por +$40.', bg: 'bg-[#FFE7D6]' },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden t-paper" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 pt-10 pb-14 md:pt-16 md:pb-24 grid lg:grid-cols-2 gap-10 lg:gap-6 items-center">
          <div className="space-y-6 relative z-[2]">
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-[#2B1A12] bg-[#FFFDF8] px-4 py-1.5 text-sm font-bold shadow-[3px_3px_0_#2B1A12] t-sticker-pop" style={{ '--d': '.1s', transform: 'rotate(-2deg)' }}>
              <span className="w-2 h-2 rounded-full bg-[#3F7D3A]" aria-hidden="true" />
              {heroBadge}
            </span>
            <HeroTitle prefix={heroPrefix} highlight={heroHighlight} />
            <p className="text-lg md:text-xl text-[#6B5444] max-w-lg leading-relaxed">{heroSubtitle}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to={heroCtaPrimaryPath} className="t-btn t-btn-primary text-lg px-7" data-testid="hero-book-btn">
                {heroCtaPrimary} <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Link>
              <Link to={heroCtaSecondaryPath} className="t-btn t-btn-ghost text-lg px-7" data-testid="hero-services-btn">
                {heroCtaSecondary}
              </Link>
            </div>
            {reviews.length ? (
              <p className="inline-flex items-center gap-2 text-sm font-semibold text-[#2B1A12]">
                <Star className="w-4 h-4 fill-[#F2B33D] text-[#F2B33D]" aria-hidden="true" />
                {avg.toFixed(1)} · {reviews.length} reseña{reviews.length !== 1 ? 's' : ''}
              </p>
            ) : null}
          </div>
          <div className="relative z-[1]">
            <HeroPizza photo={heroPhoto} brand={brand} />
          </div>
        </div>
      </section>

      <Marquee items={marqueeItems} />

      {/* Promos */}
      <section className="py-14 md:py-20" aria-labelledby="promos">
        <div className="max-w-7xl mx-auto px-4">
          <p className="font-hand text-2xl text-[#C2410C]">para tu antojo</p>
          <h2 id="promos" className="font-heading text-4xl md:text-5xl font-extrabold mb-8">Promos de la casa</h2>
          <Reveal className="flex md:grid md:grid-cols-3 gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 md:mx-0 md:px-0 t-tabs">
            {promos.map((pr, i) => (
              <article
                key={pr.title}
                className={`t-reveal snap-start shrink-0 w-[82%] sm:w-[60%] md:w-auto relative rounded-[24px] border-2 border-[#2B1A12] shadow-[6px_6px_0_#2B1A12] p-6 pt-10 ${pr.bg}`}
                style={{ '--i': i }}
              >
                <span className={`t-sticker t-wiggle absolute -top-6 right-5 text-3xl px-4 py-2 ${pr.tone}`}>{pr.sticker}</span>
                <h3 className="font-heading text-3xl font-extrabold">{pr.title}</h3>
                <p className="mt-2 text-[#2B1A12] text-base">{pr.text}</p>
                <Link to="/pedir" className="t-btn t-btn-offset mt-6 bg-[#FFFDF8]">
                  Aprovechar <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </article>
            ))}
          </Reveal>
          {/* Paquete legal (doc. 05 §8): condiciones de las promociones */}
          <p className="mt-4 text-sm text-[#6B5444]" data-testid="promo-conditions">
            2x1 en pizzas · 30% en una sola pizza. Vigencia: {legalTerms.vigencia_promos || 'hasta nuevo aviso'}. No
            acumulables entre sí ni con cupones; no aplican a bebidas, complementos ni envío.{' '}
            <Link to="/terminos#promociones" className="font-semibold underline underline-offset-2 hover:no-underline">
              Ver condiciones
            </Link>
          </p>
        </div>
      </section>

      {/* Destacadas */}
      <section className="py-14 md:py-20 bg-[#FFFDF8]" aria-labelledby="destacadas" data-testid="products-preview">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <h2 id="destacadas" className="font-heading text-4xl md:text-5xl font-extrabold">{prodTitle}</h2>
              <p className="text-[#6B5444] mt-2 text-lg">{prodIntro}</p>
            </div>
            <Link to="/pedir" className="t-btn t-btn-ghost">
              Ver todo el menú <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
          <Reveal className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured === null
              ? [0, 1, 2].map((i) => (
                  <div key={i} className="t-card overflow-hidden" aria-hidden="true">
                    <div className="t-shimmer h-52" />
                    <div className="p-5 space-y-3">
                      <div className="t-shimmer h-6 w-2/3 rounded" />
                      <div className="t-shimmer h-4 w-full rounded" />
                    </div>
                  </div>
                ))
              : featured.map((item, i) => {
                  const img = item.image_url ? resolveMediaUrl(item.image_url) : '';
                  return (
                    <article key={item.id} className="t-card t-reveal overflow-hidden flex flex-col" style={{ '--i': i }}>
                      <div className="t-card__media relative h-52 bg-gradient-to-br from-[#FFE7D6] to-[#FFF6EA] flex items-center justify-center overflow-hidden">
                        {img ? (
                          <img src={img} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
                        ) : (
                          <PizzaArt className="h-[85%] w-auto drop-shadow-lg" />
                        )}
                        {item.is_named_specialty ? (
                          <span className="t-sticker absolute top-3 left-3 text-sm">Especial</span>
                        ) : null}
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="font-heading text-2xl font-bold">{item.name}</h3>
                        {item.description ? <p className="text-[#6B5444] mt-1 line-clamp-2">{item.description}</p> : null}
                        <div className="mt-auto pt-4 flex items-center justify-between">
                          <span className="font-bold text-lg tabular">
                            <span className="text-sm font-semibold text-[#6B5444]">desde </span>${priceFrom(item).toFixed(0)}
                          </span>
                          <Link
                            to={`/pedir?item=${encodeURIComponent(item.id)}`}
                            className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#C2410C] text-white text-2xl font-bold hover:bg-[#A8360A] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#F2B33D]"
                            aria-label={`Pedir ${item.name}`}
                          >
                            +
                          </Link>
                        </div>
                      </div>
                    </article>
                  );
                })}
          </Reveal>
          <p className="mt-4 text-sm text-[#6B5444]">
            Imágenes ilustrativas. La presentación puede variar; el tamaño y los ingredientes son los descritos en el menú.
            {' '}Consulta ingredientes y alérgenos antes de pedir.
          </p>
        </div>
      </section>

      <HowItWorks />

      {/* Por qué elegirnos */}
      <section className="py-14 md:py-20 bg-[#FFFDF8]" aria-labelledby="por-que">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 id="por-que" className="font-heading text-4xl md:text-5xl font-extrabold">{servicesTitle}</h2>
            <p className="text-[#6B5444] text-lg mt-3 mb-6">{servicesIntro}</p>
            <Reveal as="ul" className="grid sm:grid-cols-2 gap-3">
              {services.map((s, i) => (
                <li key={`${s}-${i}`} className="t-reveal flex items-center gap-3 rounded-2xl bg-[#FFF6EA] border border-[#F1DFC8] px-4 py-3 font-semibold" style={{ '--i': i }}>
                  <span className="w-7 h-7 rounded-full bg-[#3F7D3A] text-white flex items-center justify-center shrink-0" aria-hidden="true">
                    <Check className="w-4 h-4" />
                  </span>
                  {s}
                </li>
              ))}
            </Reveal>
            {features.length ? (
              <Reveal className="grid grid-cols-2 gap-3 mt-6">
                {features.map((f, i) => {
                  const Ico = iconFromApiName(f.icon);
                  return (
                    <div key={`${f.title}-${i}`} className="t-reveal rounded-2xl border border-[#F1DFC8] p-4" style={{ '--i': i }}>
                      <Ico className="w-6 h-6 text-[#C2410C]" aria-hidden="true" />
                      <p className="font-heading font-bold text-lg mt-2">{f.title}</p>
                      <p className="text-sm text-[#6B5444]">{f.desc}</p>
                    </div>
                  );
                })}
              </Reveal>
            ) : null}
          </div>
          {clinicImg ? <ParallaxImage src={clinicImg} alt={brand} /> : null}
        </div>
      </section>

      {/* Reseñas */}
      <section className="py-14 md:py-20 t-paper" aria-labelledby="resenas-home" data-testid="reviews-preview">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <h2 id="resenas-home" className="font-heading text-4xl md:text-5xl font-extrabold">{reviewsTitle}</h2>
              {reviews.length ? (
                <p className="mt-2 flex items-center gap-2 text-lg font-semibold">
                  <span className="font-heading text-4xl">{avg.toFixed(1)}</span>
                  <span className="flex" aria-label={`${avg.toFixed(1)} de 5 estrellas`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-5 h-5 ${n <= Math.round(avg) ? 'fill-[#F2B33D] text-[#F2B33D]' : 'text-[#F1DFC8]'}`} aria-hidden="true" />
                    ))}
                  </span>
                  <span className="text-[#6B5444] text-base">({reviews.length})</span>
                </p>
              ) : null}
            </div>
            <Link to="/resenas" className="t-btn t-btn-ghost">
              {reviews.length ? 'Ver todas las reseñas' : 'Escribir una reseña'}
            </Link>
          </div>
          {reviews.length ? (
            <Reveal className="flex md:grid md:grid-cols-3 gap-5 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 pb-2 t-tabs">
              {reviews.slice(0, 3).map((r, i) => (
                <figure key={r.id} className="t-reveal t-card snap-start shrink-0 w-[85%] md:w-auto p-6" style={{ '--i': i }}>
                  <div className="flex" aria-label={`${r.rating} de 5 estrellas`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= r.rating ? 'fill-[#F2B33D] text-[#F2B33D]' : 'text-[#F1DFC8]'}`} aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="mt-3 text-lg">“{r.comment}”</blockquote>
                  <figcaption className="mt-3 font-hand text-2xl text-[#C2410C]">— {r.patient_name}</figcaption>
                </figure>
              ))}
            </Reveal>
          ) : (
            <div className="t-card p-8 text-center max-w-xl mx-auto">
              <PizzaArt className="w-24 h-24 mx-auto" />
              <p className="font-heading text-2xl font-bold mt-4">Sé el primero en contarnos qué tal tu pizza</p>
              <Link to="/resenas" className="t-btn t-btn-primary mt-5">Escribir reseña</Link>
            </div>
          )}
        </div>
      </section>

      {/* Ubicación y horario */}
      <section className="py-14 md:py-20 bg-[#FFFDF8]" aria-labelledby="ubicacion">
        <div className="max-w-5xl mx-auto px-4">
          <div className="t-card p-6 md:p-10 grid md:grid-cols-2 gap-8">
            <div>
              <h2 id="ubicacion" className="font-heading text-3xl md:text-4xl font-extrabold">Visítanos</h2>
              {address ? (
                <p className="mt-3 flex items-start gap-2 text-lg">
                  <MapPin className="w-5 h-5 mt-1 text-[#C2410C] shrink-0" aria-hidden="true" /> {address}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-3 mt-6">
                {address ? (
                  <a
                    className="t-btn t-btn-primary"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Cómo llegar
                  </a>
                ) : null}
                {phone ? (
                  <a className="t-btn t-btn-ghost" href={telHref(phone)}>
                    <Phone className="w-4 h-4" aria-hidden="true" /> Llamar
                  </a>
                ) : null}
                {wa ? (
                  <a className="t-btn t-btn-ghost" href={wa} target="_blank" rel="noreferrer">
                    <MessageCircle className="w-4 h-4" aria-hidden="true" /> WhatsApp
                  </a>
                ) : null}
              </div>
            </div>
            <div>
              <h3 className="font-heading text-2xl font-bold">Horario</h3>
              <ul className="mt-3 space-y-2 text-lg">
                {scheduleLines.map((l, i) => (
                  <li key={i} className="border-b border-dashed border-[#F1DFC8] pb-2">{l}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FinalCta title={ctaTitle} sub={ctaSub} label={ctaLabel} path={ctaPath} />
    </div>
  );
}
